import type { Role } from '../../shared/types';

// Resource/action permission model shared in spirit with the server-side
// can() guard. Clients are never users, so they never reach this code.
export type Resource =
  | 'people'
  | 'clients'
  | 'contacts'
  | 'projects'
  | 'assignments'
  | 'invoices'
  | 'datapoints'
  | 'onboarding'
  | 'integrations'
  | 'settings'
  | 'users'
  | 'email'
  | 'profile';

export type Action = 'read' | 'create' | 'update' | 'delete' | 'approve';

const ADMINS: Role[] = ['super_admin', 'admin'];

export function can(role: Role, action: Action, resource: Resource): boolean {
  // Org settings: only super_admin may modify; admins may view the page
  // (access list + integrations live on the same screen).
  if (resource === 'settings') {
    if (role === 'super_admin') return true;
    if (role === 'admin') return action === 'read';
    return false;
  }

  // Super admin and admin can do everything else.
  if (ADMINS.includes(role)) return true;

  if (role === 'manager') {
    switch (resource) {
      case 'projects':
      case 'clients':
      case 'contacts':
      case 'assignments':
        return true; // full CRUD
      case 'invoices':
        return true; // review + approve + read; create own too
      case 'people':
      case 'onboarding':
      case 'datapoints':
        return action === 'read';
      case 'profile':
        return true; // own profile
      case 'integrations':
      case 'users':
        return false;
    }
  }

  // staff: own profile (read/update) + own invoices (read/create).
  if (role === 'staff') {
    if (resource === 'profile') return action === 'read' || action === 'update';
    if (resource === 'invoices') return action === 'read' || action === 'create';
    return false;
  }

  return false;
}

// Whether a nav destination should be visible for a role.
export function canSeeView(role: Role, view: string): boolean {
  if (ADMINS.includes(role)) return true;
  if (role === 'manager') {
    // Managers see everything except org Settings and Email admin.
    return view !== 'settings' && view !== 'email';
  }
  // staff: just their dashboard, profile, and invoices.
  return ['dashboard', 'profile', 'invoices'].includes(view);
}

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
};
